import type {ReactNode} from 'react'
import {useMemo, useCallback, useInsertionEffect, lazy, Suspense, Component} from 'react'
import {useNavigate} from 'react-router'
import {isProductionEnv} from '@freckle/educator-entities/ts/common/helpers/common-api-helper'
import BugsnagHelper from '@freckle/educator-entities/ts/common/helpers/exception-handlers/bugsnag-helper'
import {isAnyPremium} from '@freckle/educator-entities/ts/common/logic/premium-access'
import {formattedName} from '@freckle/educator-entities/ts/users/models/teacher'
import {openSupportChat} from '@freckle/educator-materials/ts/components/support-button/teacher/support-chat'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import {educatorAcademyUser} from '@freckle/classroom/ts/common/helpers/api-helper'
import {displayNotification} from '@freckle/classroom/ts/common/helpers/helpers'
import {logout} from '@freckle/classroom/ts/common/helpers/logout'
import {Routes} from '@freckle/classroom/ts/common/routers/routes'
import {useJwt} from '@freckle/classroom/ts/hooks/use-jwt'
import {usePremiumLicenses} from '@freckle/classroom/ts/hooks/use-premium-licenses'
import {useTeacher} from '@freckle/classroom/ts/hooks/use-teacher'

const LazyHeader = lazy(async () => {
  const {FreckleRenaissanceGlobalHeader} = await import('./header-wrapper')
  return {default: FreckleRenaissanceGlobalHeader}
})

// Only the header uses these, so they're injected when it renders rather than
// render-blocking every page from index.html (e.g. chromeless pages have no header).
const HEADER_STYLESHEETS = [
  'https://ui.renaissance.com/fonts/Renaissance_Rebond_Headline/style.css',
  'https://ui.renaissance.com/fonts/Font_Awesome_6_Pro/fontawesome.min.css',
  'https://ui.renaissance.com/fonts/Font_Awesome_6_Pro/solid.min.css',
  'https://ui.renaissance.com/fonts/Font_Awesome_6_Pro/light.min.css'
]

let headerStylesheetsInjected = false

function injectHeaderStylesheets() {
  if (headerStylesheetsInjected) return
  headerStylesheetsInjected = true
  HEADER_STYLESHEETS.forEach(href => {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href
    // Must go in <head>: Font Awesome 6 also defines `.fa`, and the Font Awesome 4
    // stylesheet in <body> has to keep coming later in the cascade to win.
    document.head.append(link)
  })
}

class HeaderErrorBoundary extends Component<{children: ReactNode}, {hasError: boolean}> {
  constructor(props: {children: ReactNode}) {
    super(props)
    this.state = {hasError: false}
  }

  static getDerivedStateFromError = () => ({hasError: true})

  // eslint-disable-next-line class-methods-use-this
  componentDidCatch(error: Error, info: {componentStack: string}) {
    BugsnagHelper.logError(error, event => {
      event.addMetadata('external-component', 'renaissance-global-header')
      event.addMetadata('react', info)
    })
  }

  render() {
    if (this.state.hasError) {
      return <SafeTrans i18nKey="SHARED_COMPONENT_FAILED_TO_LOAD_DEFAULT_MESSAGE" />
    }
    return this.props.children
  }
}

export function RenaissanceGlobalHeader() {
  useInsertionEffect(injectHeaderStylesheets, [])

  const navigate = useNavigate()
  const {t} = useSafeTranslation()

  const teacher = useTeacher()
  const premiumLicenses = usePremiumLicenses()
  const jwt = useJwt()

  const premiumHasAny =
    premiumLicenses.data !== undefined ? isAnyPremium(premiumLicenses.data) : false

  const educatorAcademyHref = useMemo(
    () =>
      teacher.data !== undefined
        ? `https://renaissance.northpass.com/app?first_name=${teacher.data.firstName}&last_name=${teacher.data.lastName}&email=${teacher.data.email}`
        : undefined,
    [teacher]
  )

  const onLogout = useCallback(() => logout(), [])
  const onNavigate = useCallback(
    (evt: Event) => {
      const href = (evt as CustomEvent<string>).detail
      if (typeof href !== 'string' || href.length === 0) return
      navigate(href)
    },
    [navigate]
  )
  const onOpenEducatorAcademy = useCallback(() => {
    if (teacher.data === undefined) return

    educatorAcademyUser(teacher.data.id)
      .then(() => {
        window.open(educatorAcademyHref, '_blank', 'noopener noreferrer')
      })
      .catch(() => {
        displayNotification('error', t('NAVBAR_RESOURCES_EDUCATOR_ACADEMY_ERROR'), 2)
      })
  }, [teacher, educatorAcademyHref, t])

  return (
    <HeaderErrorBoundary>
      <Suspense fallback={null}>
        <LazyHeader
          env={isProductionEnv() ? 'prod' : 'dev'}
          t={t}
          userInitials={
            teacher.data !== undefined
              ? (teacher.data.firstName[0] ?? '') + (teacher.data.lastName[0] ?? '')
              : undefined
          }
          userName={teacher.data !== undefined ? formattedName(teacher.data) : undefined}
          userEmail={teacher.data?.email ?? undefined}
          userRole={teacher.data !== undefined ? t('TEACHER') : undefined}
          districtName={teacher.data?.districtName ?? undefined}
          userCountry={teacher.data?.countryCode ?? undefined}
          userLanguage={teacher.data?.language ?? undefined}
          waffleVisible={jwt.data !== undefined}
          jwt={jwt.data !== undefined ? JSON.stringify(jwt.data) : undefined}
          premiumHasAny={premiumHasAny}
          homeHref={Routes.ssoLogoutToRenaissance()}
          dashboardHref={Routes.dashboard()}
          profileSettingsHref={Routes.profileSettings()}
          productSettingsHref={Routes.productSettings()}
          pricingHref={Routes.pricing()}
          referralProgramHref={
            premiumHasAny ? Routes.premiumReferralProgram() : Routes.referralProgram()
          }
          educatorAcademyHref={educatorAcademyHref}
          onLogout={onLogout}
          onNavigate={onNavigate}
          onOpenSupportChat={openSupportChat}
          onOpenEducatorAcademy={onOpenEducatorAcademy}
        />
      </Suspense>
    </HeaderErrorBoundary>
  )
}
